import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaArrowDown,
  FaArrowUp,
  FaEdit,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import DeleteConfirmDialog from "../../../../components/common/DeleteConfirmDialog";
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  IconButton,
  RefreshButton,
  Switch,
  Table,
  Text,
  useDisclosure,
  VStack,
} from "../../../../components/ui";
import {
  PromptShortcut,
  promptService,
} from "../../../../services/promptService";
import { showToast } from "../../../../utils/toast";
import PromptShortcutModal from "./PromptShortcutModal";
import SuggestedShortcuts from "./SuggestedShortcuts";

const PromptShortcutsPage: React.FC = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<PromptShortcut[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<PromptShortcut | null>(null);

  const editorModal = useDisclosure();
  const deleteDialog = useDisclosure();

  const load = async () => {
    try {
      setLoading(true);
      const data = await promptService.listCompanyShortcuts();
      setItems(data);
    } catch (error: any) {
      showToast({
        title: t("app.promptShortcuts.errors.loadFailed"),
        description: error?.message,
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = () => {
    setSelected(null);
    editorModal.onOpen();
  };

  const handleEdit = (item: PromptShortcut) => {
    setSelected(item);
    editorModal.onOpen();
  };

  const handleDeleteClick = (item: PromptShortcut) => {
    setSelected(item);
    deleteDialog.onOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!selected) return;
    try {
      await promptService.deleteCompanyShortcut(selected.id);
      showToast({
        title: t("app.promptShortcuts.deletedToast", { name: selected.name }),
        status: "success",
      });
      deleteDialog.onClose();
      setSelected(null);
      await load();
    } catch (error: any) {
      showToast({
        title: t("app.promptShortcuts.errors.deleteFailed"),
        description: error?.response?.data?.message ?? error?.message,
        status: "error",
      });
    }
  };

  const handleToggleActive = async (item: PromptShortcut) => {
    try {
      const updated = await promptService.toggleCompanyShortcutStatus(
        item.id,
        !item.isActive,
      );
      setItems((prev) => prev.map((p) => (p.id === item.id ? updated : p)));
    } catch (error: any) {
      showToast({
        title: t("app.promptShortcuts.errors.toggleFailed"),
        description: error?.message,
        status: "error",
      });
    }
  };

  const handleMove = async (item: PromptShortcut, direction: "up" | "down") => {
    // Swap sort order with the neighbour. Two-row payload — the rest are left
    // untouched by the backend reorder endpoint.
    const sorted = [...items].sort(
      (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
    );
    const idx = sorted.findIndex((p) => p.id === item.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[swapIdx];
    try {
      await promptService.reorderCompanyShortcuts([
        { id: a.id, sortOrder: b.sortOrder },
        { id: b.id, sortOrder: a.sortOrder },
      ]);
      await load();
    } catch (error: any) {
      showToast({
        title: t("app.promptShortcuts.errors.reorderFailed"),
        description: error?.message,
        status: "error",
      });
    }
  };

  const sortedItems = [...items].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );

  return (
    <Box p={6}>
      <VStack align="stretch" gap={6}>
        <HStack justify="space-between">
          <Box>
            <Heading size="lg">{t("app.promptShortcuts.title")}</Heading>
            <Text color="var(--text-secondary-color)" mt={1}>
              {t("app.promptShortcuts.subtitle")}
            </Text>
          </Box>
          <HStack>
            <RefreshButton onClick={load} loading={loading} />
            <Button intent="primary" onClick={handleCreate}>
              <FaPlus /> {t("app.promptShortcuts.createCta")}
            </Button>
          </HStack>
        </HStack>

        <SuggestedShortcuts onAccepted={load} />

        <Box borderRadius="md" overflow="hidden" overflowX="auto">
          <Table.Root variant="outline" size="sm" minW="560px">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader width="40px">#</Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.promptShortcuts.table.name")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.promptShortcuts.table.body")}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="120px">
                  {t("app.promptShortcuts.table.active")}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="160px">
                  {t("app.promptShortcuts.table.actions")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {loading ? (
                <Table.Row>
                  <Table.Cell colSpan={5} textAlign="center">
                    <Text>{t("app.common.loading")}</Text>
                  </Table.Cell>
                </Table.Row>
              ) : sortedItems.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={5} textAlign="center">
                    <Text color="var(--text-secondary-color)">
                      {t("app.promptShortcuts.empty")}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                sortedItems.map((item, idx) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      <HStack gap={1}>
                        <IconButton
                          aria-label={t("app.promptShortcuts.moveUp")}
                          size="xs"
                          variant="ghost"
                          disabled={idx === 0}
                          onClick={() => handleMove(item, "up")}
                        >
                          <FaArrowUp />
                        </IconButton>
                        <IconButton
                          aria-label={t("app.promptShortcuts.moveDown")}
                          size="xs"
                          variant="ghost"
                          disabled={idx === sortedItems.length - 1}
                          onClick={() => handleMove(item, "down")}
                        >
                          <FaArrowDown />
                        </IconButton>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell>
                      <VStack align="start" gap={0}>
                        <HStack gap={2}>
                          <Text fontWeight="medium">{item.name}</Text>
                          {item.icon && (
                            <Badge
                              colorPalette="gray"
                              variant="subtle"
                              size="sm"
                            >
                              {item.icon}
                            </Badge>
                          )}
                        </HStack>
                        {item.description && (
                          <Text
                            fontSize="xs"
                            color="var(--text-secondary-color)"
                          >
                            {item.description}
                          </Text>
                        )}
                      </VStack>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        fontSize="sm"
                        color="var(--text-secondary-color)"
                        css={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.body}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Switch
                        checked={item.isActive}
                        onCheckedChange={() => handleToggleActive(item)}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <HStack gap={1}>
                        <IconButton
                          aria-label={t("app.common.edit")}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(item)}
                        >
                          <FaEdit />
                        </IconButton>
                        <IconButton
                          aria-label={t("app.common.delete")}
                          size="sm"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => handleDeleteClick(item)}
                        >
                          <FaTrash />
                        </IconButton>
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Box>
      </VStack>

      <PromptShortcutModal
        isOpen={editorModal.open}
        onClose={() => {
          editorModal.onClose();
          setSelected(null);
        }}
        onSuccess={load}
        shortcut={selected}
      />

      <DeleteConfirmDialog
        isOpen={deleteDialog.open}
        onClose={deleteDialog.onClose}
        onConfirm={handleDeleteConfirm}
        title={t("app.promptShortcuts.deleteTitle")}
        message={t("app.promptShortcuts.deleteMessage", {
          name: selected?.name,
        })}
      />
    </Box>
  );
};

export default PromptShortcutsPage;
